// 🔤 초성 검색 (2026-09-06 사용자 "초성은 카톡에서 방 이름 검색하듯이 — 채팅방 검색, 조직도 검색 등 다양하게")
//   순수 유틸 — 각 검색 지점은 기존 정규화(toLowerCase)·별칭 배열·랭킹 로직을 그대로 두고 **비교 함수만** 이걸로 바꾼다.
//   판정 순서:
//     ① 일반 부분일치(대소문자 무시) — 종전 `.includes(q)` 와 같다. 그래서 기존 검색에 회귀가 없다.
//     ② 초성 질의('ㅈㄱㅇㅁ')면 텍스트의 초성열에 부분일치 → '주간업무' 히트.
//     ③ 혼합 질의('ㅈ간업무')는 글자 단위로 — 질의 글자가 자음이면 그 자리 초성과, 완성 음절이면 그 글자와 비교.
//   ②③ 은 **질의에 자음(ㄱ-ㅎ)이 하나라도 있을 때만** 돈다 — 공백 무시 규칙이 순수 영문/완성형 질의에서
//   엉뚱한 매칭(예 'cd' 가 'abc def' 에 걸림)을 만들지 않게 하는 안전장치.
//   성능: 방 목록·멤버 목록처럼 짧은 문자열을 다건 훑는 용도. 캐시 없이 매 호출 O(text×q).
//   🔴 **정본은 이 파일**(@tpt/design) — 조직도 트리 검색(orgSearch.ts)이 써야 해서 design 패키지에 둔다.
//      tpt-client 는 `src/utils/hangul.ts` 재수출로 쓴다(호출부 import 는 종전 그대로 `../utils/hangul`).
//   🔴 런타임 import 없음 — node 타입 스트립으로 회귀 스크립트(check-hangul.mjs)가 이 파일을 직접 import 한다.

/** 현대 한글 음절의 초성 19자 — U+AC00 부터 초성 1개당 588글자 주기 */
const CHO = ['ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];
const SYL_FIRST = 0xac00;   // '가'
const SYL_LAST = 0xd7a3;    // '힣'
/** 단자음 질의는 쌍자음 초성도 잡는다 (카톡 동작 — 'ㄱㅊ' 로 '까치'가 나온다). 쌍자음 질의는 쌍자음만. */
const DOUBLE: Record<string, string> = { 'ㄱ': 'ㄲ', 'ㄷ': 'ㄸ', 'ㅂ': 'ㅃ', 'ㅅ': 'ㅆ', 'ㅈ': 'ㅉ' };

const isSyllable = (code: number) => code >= SYL_FIRST && code <= SYL_LAST;
/** 호환 자모 '자음'(U+3131 ㄱ ~ U+314E ㅎ) — 모음(U+314F ㅏ~)은 초성이 될 수 없어 제외 */
const isJamo = (ch: string) => { const c = ch.charCodeAt(0); return c >= 0x3131 && c <= 0x314e; };

/** 그 글자의 초성 — 한글 음절이면 초성, 이미 자음이면 자기 자신, 그 밖은 '' */
const choOf = (ch: string): string => {
	const code = ch.charCodeAt(0);
	if (isSyllable(code)) return CHO[Math.floor((code - SYL_FIRST) / 588)];
	return isJamo(ch) ? ch : '';
};

/** 한글 음절은 초성으로, 그 밖의 글자는 소문자 그대로 — '주간업무 Report' → 'ㅈㄱㅇㅁ report' */
export function chosung(str: string | null | undefined): string {
	let out = '';
	for (const ch of String(str ?? '')) {
		const code = ch.charCodeAt(0);
		out += isSyllable(code) ? CHO[Math.floor((code - SYL_FIRST) / 588)] : ch.toLowerCase();
	}
	return out;
}

/** 질의가 초성(자음)·공백만으로 이루어졌는가 — 'ㅈㄱㅇㅁ'·'ㅈㄱ ㅇㅁ' true / 'ㅈ간'·''·'abc'·'ㅏ' false */
export function isChosungQuery(q: string | null | undefined): boolean {
	const s = String(q ?? '').replace(/\s+/g, '');
	return s.length > 0 && [...s].every(isJamo);
}

/** 텍스트 한 글자가 질의 한 글자에 걸리는가 — 질의가 자음이면 초성끼리, 아니면 글자끼리 */
const charHit = (tc: string, qc: string): boolean => {
	if (!isJamo(qc)) return tc.toLowerCase() === qc;
	const cho = choOf(tc);
	return !!cho && (cho === qc || DOUBLE[qc] === cho);
};

const WS = /\s/;
/** 공백을 무시한 글자 단위 부분일치 — 초성 질의(②)·혼합 질의(③)를 한 번에 처리한다 */
const jamoIncludes = (text: string, q: string): boolean => {
	const hay = [...text].filter((c) => !WS.test(c));
	const needle = [...q].filter((c) => !WS.test(c));
	if (needle.length === 0) return true;
	if (needle.length > hay.length) return false;
	for (let i = 0; i <= hay.length - needle.length; i++) {
		let ok = true;
		for (let j = 0; j < needle.length; j++) {
			if (!charHit(hay[i + j], needle[j])) { ok = false; break; }
		}
		if (ok) return true;
	}
	return false;
};

/**
 * 카톡식 검색 판정 — 종전 부분일치(①)를 포함하므로 기존 `.includes(q)` 자리에 그대로 넣으면 된다.
 * 빈 질의는 항상 true(= 필터 없음), 텍스트가 비었으면 false.
 */
export function matchesHangul(text: string | null | undefined, q: string | null | undefined): boolean {
	const needle = String(q ?? '').trim().toLowerCase();
	if (!needle) return true;
	const hay = String(text ?? '').toLowerCase();
	if (!hay) return false;
	if (hay.includes(needle)) return true;                  // ① 종전 동작
	if (![...needle].some(isJamo)) return false;            // 자음이 없는 질의는 ①이 전부
	return jamoIncludes(hay, needle);                       // ② 초성 · ③ 혼합
}

/** 여러 후보 문자열 중 하나라도 걸리면 true (undefined·빈 문자열은 건너뛴다) */
export function matchesAny(fields: Array<string | null | undefined>, q: string | null | undefined): boolean {
	if (!String(q ?? '').trim()) return true;
	return fields.some((f) => !!f && matchesHangul(f, q));
}
